import React from 'react';
import { QuestionNumber } from './QuestionNumber';
import { MultipleChoiceQuestion } from './MultipleChoiceQuestion';
import { TextQuestion } from './TextQuestion';
import { EssayQuestion } from './EssayQuestion';
import { AudioIndicator } from './Listening/AudioIndicator';
import type { QuestionGroup as QuestionGroupType, MultipleChoiceQuestion as MCQ } from '@/Data/Topik/types';
import './QuestionGroup.scss';

// Styles
const IMAGE_CONTAINER_STYLE = {
    width: '100%',
    background: '#d9d9d9',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
};

const IMAGE_STYLE = {
    width: '100%',
    height: '100%',
    objectFit: 'contain' as const,
    borderRadius: '8px',
    display: 'block',
    background: '#d9d9d9'
};

const TEXT_BOX_STYLE = {
    width: '100%',
    background: '#f5f5f5',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #000',
    fontSize: '18px',
    color: '#212121',
    lineHeight: '1.5',
    letterSpacing: '0.18px',
    marginBottom: '16px',
    whiteSpace: 'pre-wrap' as const
};


const questionidToDisplay = (questionid: number) => {
    if (questionid <= 50) {
        return `L${questionid}`;
    } else if (questionid <= 100) {
        return `R${questionid - 50}`;
    } else {
        return `5${questionid - 100}`;
    }
};

interface QuestionGroupProps {
    group: QuestionGroupType;
    section: 'listening' | 'reading' | 'writing';
    selectedAnswers: string;
    onAnswerSelect: (questionId: number, answer: string) => void;
}

export const QuestionGroup: React.FC<QuestionGroupProps> = React.memo(({ 
    group, 
    section,
    selectedAnswers: selectedAnswersJSON,
    onAnswerSelect 
}) => {
    const questionIds = group.questions.map(q => q.questionid);
    const selectedAnswers = React.useMemo<Record<number, string>>(() => {
        try {
            return JSON.parse(selectedAnswersJSON);
        } catch (error) {
            console.error('Error parsing selected answers:', error);
            return {};
        }
    }, [selectedAnswersJSON]);

    return (
        <div className="QuestionGroup">
            {group.shared_question_text && (
                <div className="SampleQuestionInstruction" >
                    {questionIds.length > 0 && <QuestionNumber questionIds={questionIds} />}
                    {" "}{group.shared_question_text}
                </div>
            )}
            
            {group.shared_resource?.type === 'text' && group.shared_resource?.text && (
                <div className="SharedText">
                    {group.shared_resource.text}
                </div>
            )}

            <div className="SampleQuestionContainer" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div className="SampleQuestionBox">
                    {group.questions.map((question, qIndex) => (
                        <React.Fragment key={question.questionid}>
                            {qIndex > 0 && <div className="Divider"></div>}
                            <div className="SampleQuestionSub">
                                <div className="QuestionTitle">
                                    <span className="QuestionNumber">
                                        {`${questionidToDisplay(question.questionid)}.`}
                                    </span>
                                    {question.text && question.text.length <= 50 && <span className="QuestionText">{question.text}</span>}
                                </div>

                                {question.image && !question.image.endsWith('.html') && (
                                    <div style={IMAGE_CONTAINER_STYLE}>
                                        <img 
                                            src={question.image} 
                                            alt={`${question.questionid}번 문제 이미지`} 
                                            style={IMAGE_STYLE}
                                        />
                                    </div>
                                )}

                                {question.image && question.image.endsWith('html') && (
                                    <iframe style={IMAGE_CONTAINER_STYLE} src={question.image}
                                    title={`${question.questionid}번 문제 이미지`}
                                    />
                                )}

                                {question.text && question.text.length > 50 && question.text.split('\n\n').map( (text, index) =>
                                    <div key={index} style={TEXT_BOX_STYLE}>
                                        {text}
                                    </div>
                                )}

                                {(question as any).audio_url && section === 'listening' && (
                                    <AudioIndicator type="question" />
                                )}

                                {(question as MCQ).choices && (
                                    <MultipleChoiceQuestion
                                        question={question as MCQ}
                                        selectedAnswer={selectedAnswers[question.questionid]}
                                        onAnswerSelect={onAnswerSelect}
                                    />
                                )}

                                {(question.question_type === 'complete-dialogue' || question.question_type === 'complete-sentence') && (
                                    <TextQuestion
                                        questionId={question.questionid}
                                        value={selectedAnswers[question.questionid] || ''}
                                        onChange={onAnswerSelect}
                                        isFillInBlank={true}
                                    />
                                )}

                                {(question.question_type === 'essay-writing' || question.question_type === 'data-description') && (
                                    <EssayQuestion
                                        question={question as any}
                                        value={selectedAnswers[question.questionid] || ''}
                                        onChange={onAnswerSelect}
                                    />
                                )}
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
});